import {Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import * as _ from 'lodash';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import BusinessEntity from '../../../../../services/businessEntity.model';
import {FarmSiteService} from '../../../../../services/farmSite.service';
import FarmSiteModel from '../../../../../services/farmSite.model';
import {TransactionsService} from '../../../../../services/transactions.service';
import TransactionsModel from '../../../../../services/transactions.model';
import {TransactionTypeService} from 'src/app/services/transactionType.service';
import TransactionTypeModel from '../../../../../services/transactionType.model';
import {PondSectionService} from '../../../../../services/pondSection.service';
import PondSectionModel from '../../../../../services/pondSection.model';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import PondCultureModel from 'src/app/services/pondCulture.model';
import {ActivatedRoute} from '@angular/router';
import {ExcelService} from 'src/app/services/excelService';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';

declare var $: any;

/**
 * @component TransactionRegisterComponent
 * @selector app-transaction-register
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `TransactionRegisterComponent` is a comprehensive reporting interface designed to display and manage the
 * Transaction Register for farm operations. It serves as a central log for various activities and costs associated
 * with farm sites, sections, and ponds.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches transaction records based on hierarchical selection (Business -> Farm Site).
 * 2. **Advanced Filtering**: Provides multi-faceted filtering capabilities including:
 *    - **Location**: Filter by Section and Pond.
 *    - **Item Type**: Filter by Inventory vs. Non-Inventory items.
 *    - **Transaction Type**: Filter by specific activity types (e.g., Feed Usage, Harvest, Transfer).
 *    - **Date Range**: Filter transactions within a specific timeframe.
 *    - **Text Search**: Search across Section Name, Tank Name, Item Name, and Transaction Type.
 * 3. **Data Presentation**: Displays data in a tabular format with infinite scrolling support for performance.
 * 4. **Export**: Facilitates the export of the transaction register to an Excel file for offline analysis.
 * 5. **Role-Based Access**: Adapts initialization and data visibility based on user roles (Farmer, Farm Manager, Employee, etc.).
 *
 * ## User Roles & Permissions:
 * - **Farm Category**:
 *   - **Business Admin / Farmer**: Access to all farm sites within their business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them (via Farm Spend context).
 * - **Employee**:
 *   - Can search for and select any business entity to view reports.
 *   - Restricted from seeing certain business details if not authorized.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role (e.g., 'Business Admin', 'Farmer').
 * - `loginBusinessCategory`: Business context ('Farm').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 * - `LoginUserName`: Used for report headers.
 * - `loginPersonMobileNumber`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` checks user context.
 *    - If 'Farm', auto-loads farm sites.
 *    - If 'Employee', loads all businesses for search.
 *    - Loads available Transaction Types.
 * 2. **User Selection**: User selects Business and Farm Site (Required).
 * 3. **Optional Filtering**: User selects Section, Pond, Item Type, Transaction Type, or Date Range.
 * 4. **Data Fetching**: `transactionRegister()` calls `TransactionsService` to fetch the dataset.
 * 5. **Client-Side Processing**:
 *    - Data is stored in `dupTransactions` (master list).
 *    - `BatchLoader` handles pagination/infinite scroll into `transactions` (display list).
 *    - `onFiltersApplied` handles client-side filtering of the master list.
 *
 * ## UI State Management:
 * - **Loading**: `responseLoader` for API calls, `listLoader` for scroll loading.
 * - **Form**: `registerForm` manages required fields.
 * - **Filtering**: Flags like `isSectionSelected`, `isDateRangeSelected` track active filters.
 */
@Component({
  selector: 'app-transaction-register',
  templateUrl: './transaction-register.component.html',
  styleUrls: ['./transaction-register.component.scss']
})
export class TransactionRegisterComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<TransactionsModel>} The list of transactions currently displayed in the UI. */
  public transactions: Array<TransactionsModel>;

  /** @type {TransactionsModel} Model used for binding filter criteria (business_id, farm_site_id, etc.). */
  public transaction: TransactionsModel;

  /** @type {Array<TransactionsModel>} Master list of fetched transactions used for filtering. */
  public dupTransactions: Array<TransactionsModel>;

  /** @type {TransactionsModel} Model used for Excel export configuration (unused in current logic). */
  public transactionExcelExport: TransactionsModel;

  /** @type {Array<TransactionTypeModel>} List of available transaction types for filtering. */
  public transactionTypes: Array<TransactionTypeModel>;

  /** @type {Array<TransactionTypeModel>} Duplicate list of transaction types (unused). */
  public duplicateTransactionTypes: Array<TransactionTypeModel>;

  /** @type {Array<PondSectionModel>} List of sections in the selected farm site. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<PondCultureModel>} List of ponds in the selected section. */
  public customerPonds: Array<PondCultureModel>;

  /** @type {CustomerPondModel} Model for customer pond details. */
  public customerPond: CustomerPondModel;

  /** @type {string} The role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} The name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} The name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} The category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {Array<any>} Input data for transaction register (unused). */
  public transactionRegisterInputData = new Array();

  /** @type {boolean} Flag for sorting by Item Name. */
  public itemnameAsc: boolean = false;

  /** @type {boolean} Flag for sorting by Transaction Type. */
  public transactiontypeAsc: boolean = false;

  /** @type {any} Filter value for Inventory type (1 for Inventory, 0 for Non-Inventory). */
  public isInventory: any;

  /** @type {boolean} Flag controlling the main loading spinner for API requests. */
  public responseLoader: boolean;

  /** @type {{} & Array<TransactionsModel>} Original data reference (unused). */
  public originaltransactionRegisterData: {} & Array<TransactionsModel>;

  /** @type {boolean} Flag for sorting by Transaction Date. */
  public transactionregisterAsc: boolean;

  /** @type {number} Offset for API pagination (default 0). */
  public offset = 0;

  /** @type {number} Limit for API pagination (default 10000). */
  public limit = 10000;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {string} The current search string entered by the user. */
  public search: string;

  /** @type {boolean} Flag to display a "No Data" message. Default is `false`. */
  public NoDataMessage: boolean = false;

  /** @type {number} The current active page number. */
  public activePageNo: number;

  /** @type {FormGroup} The main reactive form for business and location selection. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling the visibility of the business selection dropdown. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Placeholder or value for the business search input. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public submitted: boolean;

  /** @type {FormGroup} Reactive form for the date range picker. */
  dateRangeForm!: FormGroup;

  /** @type {moment.Moment} The maximum allowed date for the date picker (current date). */
  maxDate = moment();

  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} The currently selected date range. */
  selected: {startDate: moment.Moment, endDate: moment.Moment};

  /** @type {boolean} Flag to disable the submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {number} Current tab index (unused). */
  public tabIndex: number = 0;

  /** @type {boolean} Flag indicating if the search button has been clicked. */
  public isSearchClicked: boolean;

  /** @type {string} The type of filter currently active (unused). */
  public filterType: string;

  /** @type {boolean} Flag indicating if the submit action has been triggered successfully. */
  public isSubmitClicked: boolean;

  /** @type {boolean} Flag indicating if a section is selected for filtering. */
  public isSectionSelected: boolean;

  /** @type {boolean} Flag indicating if a pond is selected for filtering. */
  public isPondSelected: boolean;

  /** @type {boolean} Flag indicating if an item type (inventory) is selected for filtering. */
  public isItemTypeSelected: boolean;

  /** @type {boolean} Flag indicating if a transaction type is selected for filtering. */
  public isTransactionTypeSelected: boolean;

  /** @type {boolean} Flag indicating if a date range is selected for filtering. */
  public isDateRangeSelected: boolean;

  /** @type {boolean} Flag controlling the spinner for list batch loading. Default is `false`. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} The starting index for batch loading. Default is `0`. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if client-side filtering is currently active. Default is `false`. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if the list is empty. Default is `false`. */
  public noList: boolean = false;

  /** @type {BatchLoader<any>} Utility for handling batch loading of data. */
  private batchLoader: BatchLoader<any>;

  /**
   * Constructor for TransactionRegisterComponent.
   * Initializes the component and injects necessary services.
   *
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSitesService - Service for farm site operations.
   * @param {TransactionsService} transactionService - Service for fetching transaction data.
   * @param {PondSectionService} pondSectionService - Service for pond section operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {TransactionTypeService} transactionTypesService - Service for transaction type operations.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service FormBuilder
   * @service ExcelService
   * @service ActivatedRoute
   * @service Sidenavaction
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service TransactionsService
   * @service PondSectionService
   * @service CustomerPondService
   * @service TransactionTypeService
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public excelService: ExcelService, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public transactionService: TransactionsService, public pondSectionService: PondSectionService, public customerPondService: CustomerPondService, public transactionTypesService: TransactionTypeService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.transaction = new TransactionsModel();
    this.customerPond = new CustomerPondModel();
    // Initialize the main register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required]
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes the date picker.
   * - Sets up the previous route.
   * - Determines the logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection (for Employees).
   * - Fetches available transaction types.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.search = '';
      this.filterType = undefined;
      this.isSubmitClicked = false;
      this.isDateRangeSelected = this.selected == undefined ? false : true;
      this.initializedatePicker();
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      switch (localStorage.getItem('loginBusinessCategory')) {
        case 'Farm':
          this.transaction.business_id = localStorage.businessID;
          this.registerForm.get('businessfield').setValidators(null);
          this.registerForm.get('businessfield').updateValueAndValidity();
          switch (this.loginPersonType) {
            case 'Business Admin':
            case 'Farmer':
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(localStorage.businessID);
              break;
            default:
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
              break;
          }
          if (this.farmSites.length === 1) {
            this.transaction.farm_site_id = this.farmSites[0].id; // model-binding key
            this.getSetionsByFarmsiteId(this.transaction.farm_site_id);
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.businessEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
      this.transactionTypes = await this.transactionTypesService.getAllTransactionTypes();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businessEntities` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @async
   * @description Handles the selection of a business entity from the dropdown.
   * - Updates the form value and model.
   * - Fetches farm sites associated with the selected business.
   * - Auto-selects the farm site if only one exists.
   * @param {BusinessEntity} business - The selected business entity.
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.transaction.business_id = business.id;
    if (this.transaction.business_id) {
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.transaction.business_id);
      if (this.farmSites.length === 1) {
        this.transaction.farm_site_id = this.farmSites[0].id; // model-binding key
        this.getSetionsByFarmsiteId(this.transaction.farm_site_id);
      }
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Fetches sections associated with a specific farm site ID.
   * @param {string} farm_site_id - The ID of the farm site.
   * @side-effect Updates `pondSections`.
   */
  public async getSetionsByFarmsiteId(farm_site_id: string) {
    try {
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farm_site_id);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Fetches ponds associated with the selected section ID.
   * Filters for non-preparatory ponds and formats the data for the dropdown.
   * @returns {Promise<Array<any>>} The list of processed customer ponds.
   * @side-effect Updates `customerPonds`.
   */
  public async getPondsBySectionId() {
    try {
      this.transaction.pond_id = undefined;
      let customerPondsData = new Array();
      if (this.transaction.section_id) {
        let customerPondData = await this.customerPondService.getAllPondsBySectionId(this.transaction.section_id);
        if (customerPondData && customerPondData.length > 0) {
          for (let i = 0; i < customerPondData.length; i++) {
            if (customerPondData[i].isPreparatory == false && (customerPondData[i].PondCultureData && customerPondData[i].PondCultureData.length > 0)) {
              for (let j = 0; j < customerPondData[i].PondCultureData.length; j++) {
                customerPondData[i].PondCultureData[j].pond_name = customerPondData[i].name;
                customerPondData[i].PondCultureData[j].pond_id = customerPondData[i].id;
                customerPondsData.push(customerPondData[i].PondCultureData[j]);
              }
            }
          }
          this.customerPonds = customerPondsData;
          return customerPondsData;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onFilterChange
   * @description Resets filter variables when a primary filter changes.
   * @side-effect Calls `emptyFilterVariables`.
   */
  public onFilterChange() {
    this.filterType = '';
    this.emptyFilterVariables();
  }

  /**
   * @method emptyFilterVariables
   * @description Resets all filter-related flags and model properties to their default states.
   * @side-effect Resets `isSectionSelected`, `isPondSelected`, `transaction.section_id`, etc.
   */
  public emptyFilterVariables() {
    this.isSectionSelected = false;
    this.isPondSelected = false;
    this.isItemTypeSelected = false;
    this.isTransactionTypeSelected = false;
    this.isDateRangeSelected = false;
    this.transaction.section_id = undefined;
    this.transaction.pond_id = undefined;
    this.isInventory = undefined;
    this.transaction.transaction_type_id = undefined;
    this.selected = undefined;
  }

  /**
   * @method onFiltersApplied
   * @description Applies filters to the transaction list.
   * This is a central method that orchestrates filtering based on:
   * - Text search (`search` property).
   * - Specific filters (Section, Pond, Item Type, Transaction Type, Date Range).
   * If no filters are active, it resets the list to the initial batch loaded state.
   * @side-effect Updates `transactions`, `isFiltering`, `batchLoader`.
   */
  public onFiltersApplied() {
    this.transactions = [];
    this.isFiltering = true;
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.filterType == '' && (this.isSectionSelected || this.isPondSelected || this.isItemTypeSelected || this.isDateRangeSelected || this.isTransactionTypeSelected)) {
        this.applySearchWithFilterType(value);
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.filterType == '' && (this.isSectionSelected || this.isPondSelected || this.isItemTypeSelected || this.isDateRangeSelected || this.isTransactionTypeSelected)) {
        this.applySearchWithFilterType(value);
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * @method applySearchFilter
   * @private
   * @description Filters the transaction list based on a search string. Checks 'section_name', 'tank_name', 'item_name', 'transaction_type'.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchFilter(searchValue: string) {
    this.transactions = this.dupTransactions.filter((transactionsObj: any) =>
      ['section_name', 'tank_name', 'item_name', 'transaction_type'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], transactionsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * @method applySearchWithFilterType
   * @private
   * @description Applies both specific criteria filters (Section, Pond, Date, etc.) and text search filtering simultaneously.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchWithFilterType(searchValue: string) {

    this.transactions = this.dupTransactions.filter((transactionsObj: any) => {
      let includeConditions = true; // Initialize shouldKeep as true

      if (this.isSectionSelected && transactionsObj.section_id != this.transaction.section_id) {
        includeConditions = false; // Set shouldKeep to false if condition is not met
      }
      if (this.isPondSelected && transactionsObj.pond_id != Number(this.transaction.pond_id)) {
        includeConditions = false;
      }
      if (this.isItemTypeSelected && transactionsObj.inventory != Number(this.isInventory)) {
        includeConditions = false;
      }
      if (this.isTransactionTypeSelected && transactionsObj.transaction_type_id != this.transaction.transaction_type_id) {
        includeConditions = false;
      }
      if (this.selected && this.selected.startDate && this.selected.endDate) {
        this.isDateRangeSelected = true;
        const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
        const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
        const transactionDate = new Date(transactionsObj.transaction_date.split('T')[0]);
        if (!(transactionDate >= startDate && transactionDate <= endDate)) {
          includeConditions = false;
        }
      }

      const includesSearch = ['section_name', 'tank_name', 'item_name', 'transaction_type'].some(property => {
          const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], transactionsObj);
          return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      });

      return this.search != '' ? includeConditions && includesSearch : includeConditions;
    });
  }

  /**
   * @method clearFilters
   * @description Clears specific filters based on the input type.
   * @param {string} filterFrom - The source of the clear action ('search' or default for all filters).
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.filterType = undefined;
        this.emptyFilterVariables();
        this.onFiltersApplied();
        break;
    }
    // Reset date range form here 👇
    this.dateRangeForm.reset();
    this.selected = undefined;
  }

  /**
   * @method transactionRegister
   * @async
   * @description Fetches the transaction register data from the server.
   * - Validates the form.
   * - Resets filters and search states.
   * - Calls the service to get data (limit set to 500,000).
   * - Initializes the batch loader for pagination.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async transactionRegister() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }

    this.submitted= false;
    this.dupTransactions = [];
    this.transactions = [];
    this.isFiltering = false;
    this.isSubmitClicked = false;
    this.onFilterChange();
    this.search = '';
    this.filterType = undefined;
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      const transactionRegisterReqBody = {
        "business_id": this.transaction.business_id,
        "farm_site_id": this.transaction.farm_site_id,
        "inventory": [1, 0],
        "isBH": 0,
        "offset": 0,
        "limit": 500000,
      }
      this.dupTransactions = await this.transactionService.getTransactionRegister(transactionRegisterReqBody);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.submitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
      this.clearDateRange();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.responseLoader = false;
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.dupTransactions,
      (updatedList) => {
        this.transactions = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

   /**
    * @method
    * @description Toggles the fullscreen mode for the element with ID 'zoomView'.
    * @returns {void}
    * This method is used to Opens the specified element in fullscreen mode if the browser supports it.
    */
   public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * @method exportExcel
   * @async
   * @description Generates and downloads an Excel report of the transaction register.
   * Constructs the request body with current context and user details.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async exportExcel() {
    const transactionRegisterExcelReqBody = {
      "business_id": this.transaction.business_id,
      "farm_site_id": this.transaction.farm_site_id,
      "businessName": this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
      "farmNameAtLocation": this.farmSites.find((farmSiteObj: FarmSiteModel) => farmSiteObj.id == this.transaction.farm_site_id).farmNameAtLocation,
      "inventory": [1,0],
      "isBH": 0,
      "personName": localStorage.getItem('LoginUserName'),
      "mobileNumber": localStorage.getItem('loginPersonMobileNumber')
    }
    try {
      const fileObj = this.transactionService.excelReportsDownload('transaction', transactionRegisterExcelReqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Transactions Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method sortingTanks
   * @description Sorts the displayed transaction list based on the specified type.
   * Supports sorting by 'date', 'itemname', and 'transactiontype'.
   * @param {string} type - The field to sort by.
   */
  public sortingTanks(type: string) {
    if (type == 'date') {
      this.transactionregisterAsc = !this.transactionregisterAsc;
      let orderbyval = 'desc';
      if (this.transactionregisterAsc == true) {
        orderbyval = 'asc';
      }
      this.transactions = _.orderBy(this.transactions, ['transaction_date'], [orderbyval]);
      this.itemnameAsc = false;
      this.transactiontypeAsc = false;
    }
    if (type == 'itemname') {
      this.itemnameAsc = !this.itemnameAsc;
      let orderbyval = 'desc';
      if (this.itemnameAsc == true) {
        orderbyval = 'asc';
      }
      this.transactions = _.orderBy(this.transactions, ['item_name'], [orderbyval]);
      this.transactionregisterAsc = false;
      this.transactiontypeAsc = false;
    }
    if (type == 'transactiontype') {
      this.transactiontypeAsc = !this.transactiontypeAsc;
      let orderbyval = 'desc';
      if (this.transactiontypeAsc == true) {
        orderbyval = 'asc';
      }
      this.transactions = _.orderBy(this.transactions, ['transaction_type'], [orderbyval]);
      this.transactionregisterAsc = false;
      this.itemnameAsc = false;
    }
  }

  /**
   * @method initializedatePicker
   * @description Initializes the `dateRangeForm` and subscribes to value changes to manage `isDateRangeSelected` state.
   * @side-effect Creates `dateRangeForm`.
   */
  public initializedatePicker() {
    this.dateRangeForm = this.formBuilder.group({
      start: [null],
      end: [null]
    });

    this.dateRangeForm.valueChanges.subscribe(val => {
      const { start, end } = val;
      if (!start || !end) {
        this.isDateRangeSelected = false;
      }
    });
  }

  /**
   * @method onPickerClosed
   * @description Handles the closing event of the date picker.
   * Ensures a valid range is selected (defaults end date to start date if missing) and triggers filtering.
   * @side-effect Updates `dateRangeForm` value, `isDateRangeSelected`, and calls `onDateChanged`.
   */
  public onPickerClosed(): void {
    const { start, end } = this.dateRangeForm.value;

    if (start && !end) {
      // Only start selected → default end = start
      this.dateRangeForm.patchValue({ end: start }, { emitEvent: false });
      this.isDateRangeSelected = true;
      this.onDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.isDateRangeSelected = true;
      this.onDateChanged({ startDate: start, endDate: end });
    } else {
      this.isDateRangeSelected = false;
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the `selected` date range property and triggers filter application.
   * @param {{ startDate: Date; endDate: Date }} range - The selected date range.
   * @side-effect Updates `selected` and calls `onFiltersApplied`.
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
    this.onFiltersApplied(); // reuse your logic
  }

  /**
   * @method clearDateRange
   * @description Resets the date range form and clears the date filter.
   * @side-effect Resets `dateRangeForm`, `isDateRangeSelected`, `selected`, and calls `onFiltersApplied`.
   */
  public clearDateRange(): void {
    this.dateRangeForm.reset();
    this.isDateRangeSelected = false;
    this.selected = undefined;
    this.onFiltersApplied();
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests and cleans up the batch loader to prevent memory leaks.
   * @side-effect Cancels HTTP requests, destroys `batchLoader`.
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.transactionTypesService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.transactionService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
